'use client';

import { useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import contentData from '@/lib/constants/content';
import { useCart } from '@/contexts/CartContext';

// Shown via data-visible, not :focus — Osano .focus() matches :focus on iOS.
const linkClass =
  'sr-only rounded bg-white px-4 py-2 text-black data-[visible]:not-sr-only data-[visible]:absolute data-[visible]:left-4 data-[visible]:top-4 data-[visible]:z-50';

// Osano grabs focus around load and again near 10s. Outside that window,
// non-Tab focus (browser chrome, assistive tech) is left alone.
const OSANO_BOOTSTRAP_WINDOW_MS = 15000;

let tabPressed = false;
let inBootstrapWindow = false;
if (typeof window !== 'undefined') {
  window.addEventListener(
    'keydown',
    (e) => {
      if (e.key !== 'Tab') return;
      tabPressed = true;
      // Tab focus fires synchronously in this turn. Clearing on the next
      // macrotask avoids a stuck flag when keyup is lost (Tab into chrome,
      // or the page blurs while Tab is held).
      setTimeout(() => {
        tabPressed = false;
      }, 0);
    },
    true,
  );
}

const onFocus = (e) => {
  if (tabPressed) e.currentTarget.dataset.visible = '';
  else if (inBootstrapWindow) e.currentTarget.blur();
};

const onBlur = (e) => {
  delete e.currentTarget.dataset.visible;
};

const SkipLink = ({
  href = '#main-content',
  children = contentData.skipLink.skipToContent['en-us'],
}) => {
  const pathname = usePathname();
  const { cart } = useCart();
  const { skipToCart, skipToCO } = contentData.skipLink;

  useEffect(() => {
    // Osano runs beforeInteractive, so it can focus the link before this mounts.
    if (document.activeElement?.getAttribute('href') === href) {
      document.activeElement.blur();
    }

    inBootstrapWindow = true;
    const closeBootstrapWindow = setTimeout(() => {
      inBootstrapWindow = false;
    }, OSANO_BOOTSTRAP_WINDOW_MS);
    return () => {
      clearTimeout(closeBootstrapWindow);
      inBootstrapWindow = false;
    };
  }, [href]);

  // Route-based, not cart-based: hide "Go to cart" on the cart page itself.
  const showCart = pathname !== '/cart';
  // Checkout also needs a live Shopify URL, which is empty until items are added.
  const checkoutUrl = cart?.checkoutUrl;
  const showCheckout = checkoutUrl && cart?.totalQuantity > 0;

  return (
    <>
      {/* Same-page fragment: must stay a native <a>, next/link would intercept
          the navigation and the browser would never move focus to the target. */}
      <a href={href} className={linkClass} onFocus={onFocus} onBlur={onBlur}>
        {children}
      </a>
      {showCart && (
        <Link href="/cart" className={linkClass} onFocus={onFocus} onBlur={onBlur}>
          {skipToCart['en-us']}
        </Link>
      )}
      {showCheckout && (
        <a href={checkoutUrl} className={linkClass} onFocus={onFocus} onBlur={onBlur}>
          {skipToCO['en-us']}
        </a>
      )}
    </>
  );
};

export default SkipLink;
